// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { formatDate } from '@loa/theme';
import type { ClientDetailDTO } from '../../types/clientList';
import { Card, CardHeader, CardBody } from '../Card';
import '../../pages/ClientDetailPage.css';

type ClientOverviewTabProps = Readonly<{
  client: ClientDetailDTO;
}>;

const formatEnum = (value?: string): string => {
  if (!value) return '-';
  return value.charAt(0) + value.slice(1).toLowerCase().replaceAll('_', ' ');
};

export function ClientOverviewTab({ client }: ClientOverviewTabProps) {

  const getFullAddress = (): string => {
    const parts = [
      client.addressLine1,
      client.addressLine2,
      client.addressLine3,
      [client.townCity, client.stateProvinceCounty].filter(Boolean).join(' '),
      client.postalCode,
    ].filter((p) => p && p.length > 0);
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  const labelClass = "text-black text-base font-medium break-words font-sans";
  const valueClass = "text-black text-base font-normal break-words font-sans";

  return (
    <div>
      <Card className="flex-1">
        <CardHeader>
          <div className='text-black text-body-01 font-medium leading-normal font-sans'>Client information</div>
        </CardHeader>
        <CardBody className="gap-lg justify-between">
          <div className="flex flex-row gap-lg">
            {/* Left column */}
            <div className="flex flex-col gap-lg w-[528px]">
              <div className="flex flex-row gap-xs">
                <div className={labelClass}>First Name:</div>
                <div className={valueClass}>{client.firstName || '-'}</div>
              </div>
              <div className="flex flex-row gap-xs">
                <div className={labelClass}>Last Name:</div>
                <div className={valueClass}>{client.lastName || '-'}</div>
              </div>
              <div className="flex flex-row gap-xs">
                <div className={labelClass}>Email:</div>
                <div className={valueClass}>{client.email || '-'}</div>
              </div>
              <div className="flex flex-row gap-xs">
                <div className={labelClass}>Phone:</div>
                <div className={valueClass}>{client.phone || '-'}</div>
              </div>
            </div>

            {/* Right column */}
            <div className="flex flex-col gap-lg w-[528px]">
              <div className="flex flex-row gap-xs">
                <div className={labelClass}>Address:</div>
                <div className={valueClass}>{getFullAddress()}</div>
              </div>
              <div className="flex flex-row gap-xs">
                <div className={labelClass}>Date of Birth:</div>
                <div className={valueClass}>{client.dateOfBirth ? formatDate(client.dateOfBirth) : '-'}</div>
              </div>
              <div className="flex flex-row gap-xs">
                <div className={labelClass}>Gender:</div>
                <div className={valueClass}>{formatEnum(client.gender)}</div>
              </div>
              <div className="flex flex-row gap-xs">
                <div className={labelClass}>Marital Status:</div>
                <div className={valueClass}>{formatEnum(client.maritalStatus)}</div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
